import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,  Box, Typography, Card, CardContent, CardHeader } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { green, red } from '@mui/material/colors';

const permissions = [
    { name: 'Create a Group', observer: false, contributor: false, analyst: false, admin: true },
    { name: 'Read a Group', observer: true, contributor: true, analyst: true, admin: true },
    { name: 'Update a Group', observer: false, contributor: false, analyst: false, admin: true },
    { name: 'Delete a Group', observer: false, contributor: false, analyst: false, admin: true },
    { name: 'Manage user access to a Group', observer: false, contributor: false, analyst: false, admin: true },
    { name: 'Create a Resource', observer: false, contributor: false, analyst: true, admin: true },
    { name: 'Read a Resource', observer: true, contributor: true, analyst: true, admin: true },
    { name: 'Update a Resource', observer: false, contributor: false, analyst: true, admin: true },
    { name: 'Delete a Resource', observer: false, contributor: false, analyst: true, admin: true },
    { name: 'Provide Safety Assessments', observer: false, contributor: true, analyst: true, admin: true },
];


const footnoteClicked = (itemNum: number): void => {
    let element = document.getElementById('footnote' + itemNum);
    if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest'});
    }
};

const getSuperscript = (permissionName: String): React.ReactElement => {
    switch (permissionName) {
        case 'Create a Group':
            return <sup>
                <a href="javascript:void(0);" onClick={(): void => footnoteClicked(2)}>2</a>,
                <a href="javascript:void(0);" onClick={(): void => footnoteClicked(3)}> 3</a>
            </sup>;
        case 'Delete a Group':
            return <sup>
                <a href="javascript:void(0);" onClick={(): void => footnoteClicked(4)}>4</a>
            </sup>;
        case 'Manage user access to a Group':
            return <sup>
                <a href="javascript:void(0);" onClick={(): void => footnoteClicked(5)}>5</a>
            </sup>;
        case 'Create a Resource':
            return <sup>
                <a href="javascript:void(0);" onClick={(): void => footnoteClicked(6)}>6</a>
            </sup>;
        default:
            return <></>;
    }
};

const GroupRolePermissionEditor: React.FC = () => {
    const [permissionData] = React.useState(permissions);

    return (
        <Box sx={{ width: '90%', paddingLeft: '20px' }}>
            <Typography variant="h4" component="div" gutterBottom>
                Group Role Permissions
            </Typography>
            <Typography variant="body2" gutterBottom sx={{maxWidth: '80%'}}>
                In order to access resources such as models and occurrence reports, users must belong
                to the Group that contains the resources. Group administrators have the authority to
                add users to a Group and to assign a role for the user. Below is a table of permissions
                assigned to a user for each role that a user can have.
            </Typography>
            <br></br>
            <TableContainer component={Paper} sx={{mb: 4, maxWidth: '80%'}}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" colSpan={5}>
                                <Typography variant="h5">Permissions by Group Role
                                    <sup>
                                        <a href="javascript:void(0);" onClick={(): void => footnoteClicked(1)}>1</a>
                                    </sup>
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell><Typography variant='button'>Permission</Typography></TableCell>
                            <TableCell align='center'><Typography variant='button'>OBSERVER</Typography></TableCell>
                            <TableCell align='center'><Typography variant='button'>CONTRIBUTOR</Typography></TableCell>
                            <TableCell align='center'><Typography variant='button'>ANALYST</Typography></TableCell>
                            <TableCell align='center'><Typography variant='button'>ADMIN</Typography></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {permissionData.map((permission) => (
                            <TableRow key={permission.name}>
                                <TableCell>
                                    {permission.name} {getSuperscript(permission.name)}
                                </TableCell>
                                <TableCell align='center'>
                                    {permission.observer ? <CheckCircleOutlineIcon style={{ color: green[500] }}/> : <HighlightOffIcon style={{ color: red[500] }}/>}
                                </TableCell>
                                <TableCell align='center'>
                                    {permission.contributor ? <CheckCircleOutlineIcon style={{ color: green[500] }}/> : <HighlightOffIcon style={{ color: red[500] }}/>}
                                </TableCell>
                                <TableCell align='center'>
                                    {permission.analyst ? <CheckCircleOutlineIcon style={{ color: green[500] }}/> : <HighlightOffIcon style={{ color: red[500] }}/>}
                                </TableCell>
                                <TableCell align='center'>
                                    {permission.admin ? <CheckCircleOutlineIcon style={{ color: green[500] }}/> : <HighlightOffIcon style={{ color: red[500] }}/>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Card sx={{ padding: '10px', marginBottom: '10px', maxWidth: '80%' }}>
                <CardHeader title='Footnotes' />
                <CardContent sx= {{fontSize: '0.8rem', color: 'text.secondary' }}>
                    <ol>
                        <li id="footnote1">
                            <Typography variant='body2' >
                        Roles (and therefore permissions) in a Group hierarchy are inherited. If we consider
                        the Group hierarchy <em>A &gt; B</em>, for example, where Group <em>A</em> contains
                        Group <em>B</em>, a user with an OBSERVER role for Group <em>A</em> will automatically have
                        an OBSERVER role for Group <em>B</em> because the role is inherited from Group <em>A</em>.
                            </Typography>  
                        </li>
                        <li id="footnote2">
                            <Typography variant='body2'>
                        All users can create a Group at the top-level.
                            </Typography>
                        </li>
                        <li id="footnote3">
                            <Typography variant='body2'>
                        If a user creates a Group then he or she becomes the administrator of the Group.
                            </Typography>
                        </li>
                        <li id="footnote4">
                            <Typography variant='body2'>
                        Deleting a Group entails not only deleting the Group
                        itself, but also all Groups contained within the Group
                        and all resources contained within the Group and its Groups.
                            </Typography>
                        </li>
                        <li id="footnote5">
                            <Typography variant='body2'>
                        Managing user access to a Group means the having the ability to add users to and to remove
                        users from the Group and to set the role that a user has in the Group.
                            </Typography>
                        </li>
                        <li id="footnote6">
                            <Typography variant='body2'>
                        Resources include Groups, occurrence reports, traffic data, and so forth.
                            </Typography>
                        </li>
                    </ol>

                </CardContent>

            </Card>
        </Box>
    );
};

export default GroupRolePermissionEditor;
